import React from 'react'
import clsx from 'clsx'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { CloseCircle, Edit2, MessageText } from 'iconsax-react'
import { useTheme } from '@mui/material'

import Button from '@SonarCX/components/atoms/Button'
import Tooltip from '@SonarCX/components/atoms/Tooltip'
import TaskCommentsPreviewModal from '@SonarCX/components/atoms/TaskCommentsPreviewModal'

import { formatDate } from '@SonarCX/timeUtilities'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { useTaskComments } from '@SonarCX/providers/TaskCommentsProvider'
import useTaskInputOpen from '@SonarCX/hooks/useTaskInputOpen'
import useTaskPermissions from '@SonarCX/hooks/permissions/useTaskPermissions'

import useStyles from './styles'

const DELAY = 100

const ContentElem: React.FC<any> = React.memo(
  ({ halt, value, task, taskClick }) => {
    const contentRef = React.useRef()
    const [isCutoff, setIsCutoff] = React.useState(false)

    const tooltipTitle = React.useMemo(() => {
      const closedText = `Closed on ${formatDate(task.closed_at, 'MMM d, y')}`
      if (task.closed_at && isCutoff) {
        return (
          <>
            <div>{value}</div>
            <div>{closedText}</div>
          </>
        )
      }

      if (task.closed_at) {
        return closedText
      }

      if (isCutoff) {
        return value
      }

      return null
    }, [task.closed_at, value, isCutoff])

    React.useLayoutEffect(() => {
      const element = contentRef.current as any
      if (!element) {
        return
      }

      setIsCutoff(element.offsetWidth < element.scrollWidth)
    }, [])

    return (
      <Tooltip title={tooltipTitle} placement='right' arrow>
        <div ref={contentRef} className='content'>
          <span
            title={tooltipTitle}
            onClick={e => {
              halt(e)
              taskClick(task)
            }}
          >
            {value}
          </span>
        </div>
      </Tooltip>
    )
  }
)

const SubtaskTitle: React.FC<any> = ({
  task,
  taskClick,
  mobile,
  queueForDelete,
  isDeleting
}) => {
  const dispatch = useAppDispatch()

  const { hasModalWithComments, setHasModalWithComments, loadComments } = useTaskComments()
  const { updateTask } = useTaskAPI()
  const { canEdit } = useTaskPermissions(task)
  const { isOpen, handleClick, handleClose } = useTaskInputOpen(task, 'TITLE')

  const [shouldCheckClose, setShouldCheckClose] = React.useState(false)
  const [startedLoad, setStartedLoad] = React.useState(false)
  const [showCommentPreview, setShowCommentPreview] = React.useState(false)
  const [hovering, setHovering] = React.useState(false)
  const [startingValue, setStartingValue] = React.useState(task.title)
  const [value, setValue] = React.useState(startingValue)
  const inputRef = React.useRef(null)
  const commentsRef = React.useRef(null)
  const hoverTime = React.useRef(null)
  const hoverTimeOut = React.useRef(null)

  const theme = useTheme()
  const classes = useStyles({
    canEdit,
    closed: Boolean(task.closed_at),
    isDeleting
  })

  React.useEffect(() => {
    setStartingValue(task.title)
    setValue(task.title)
  }, [task.title])

  // reset because of timeout conflicts
  React.useEffect(() => {
    if (!showCommentPreview && shouldCheckClose) {
      setShouldCheckClose(false)
    }
  }, [showCommentPreview, shouldCheckClose])

  const halt = React.useCallback(e => {
    e.stopPropagation()
    e.preventDefault()
  }, [])

  const handleBlur = React.useCallback(
    async e => {
      try {
        halt(e)

        setTimeout(() => {
          handleClose()
        }, DELAY)

        if (startingValue !== value) {
          const newTask = { ...task, title: value }

          await updateTask(newTask.id, newTask)
          setValue(value)
          dispatch(AddSuccessAlert('Changes saved'))
        } else {
          setValue(startingValue)
        }
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
        setValue(startingValue)
      }
    },
    [halt, dispatch, startingValue, value, task, updateTask, handleClose]
  )

  const onTaskClick = React.useCallback(() => {
    if (isOpen && !mobile) {
      handleClose()
    } else {
      taskClick(task)
    }
  }, [isOpen, mobile, handleClose, taskClick, task])

  const handleHideCommentPreview = React.useCallback(() => {
    setHasModalWithComments(false)
    setShowCommentPreview(false)
    setShouldCheckClose(false)
  }, [setHasModalWithComments])

  const handleMouseEnter = React.useCallback(() => {
    if (hasModalWithComments) return
    clearTimeout(hoverTimeOut.current)
    setShouldCheckClose(false)
    hoverTime.current = setTimeout(() => {
      setShowCommentPreview(true)
      if (!startedLoad) {
        setStartedLoad(true);
        loadComments(task.id);
      }
    }, 500)
  }, [setShowCommentPreview, hasModalWithComments, loadComments, task.id, startedLoad])

  const handleMouseLeave = React.useCallback(() => {
    if (hasModalWithComments) return
    clearTimeout(hoverTime.current)
    hoverTimeOut.current = setTimeout(() => {
      setShouldCheckClose(true)
    }, 250)
  }, [hoverTime, hasModalWithComments])

  return (
    <>
      <div
        style={isOpen ? { flex: '1' } : {}}
        onClick={onTaskClick}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        className={classes.root}
      >
        {isOpen && canEdit ? (
          <div onClick={halt} className={classes.input}>
            <input
              ref={inputRef}
              placeholder='Title'
              value={value}
              onChange={e => setValue(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={e => e.key === 'Enter' && handleBlur(e)}
              autoFocus
            />

            <Button onClick={handleClose} className='button'>
              SAVE
            </Button>
          </div>
        ) : (
          <div
            onClick={onTaskClick}
            className={clsx(classes.title, classes.titleBold)}
          >
            <ContentElem
              classes={classes}
              halt={halt}
              value={value}
              task={task}
              taskClick={taskClick}
            />

            {hovering && canEdit && !mobile ? (
              <>
                <Edit2
                  onClick={e => {
                    halt(e)
                    handleClick(e)
                  }}
                  className={classes.edit}
                  size={theme.size.sm}
                />
                <CloseCircle
                  className={classes.edit}
                  onClick={e => {
                    halt(e)
                    queueForDelete(task.id)
                  }}
                  size={theme.size.sm}
                />
              </>
            ) : (
              <div style={{ width: '3rem' }} />
            )}
            <div className={classes.details}>
              {task?.comments > 0 && (
                <div
                  ref={commentsRef}
                  className={clsx(
                    classes.count,
                    classes.hoverableIconContainer,
                    showCommentPreview && classes.isViewing
                  )}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onClick={halt}
                >
                  <span className='count'>{task?.comments}</span>

                  <span className='icon'>
                    <MessageText />
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showCommentPreview && (
        <TaskCommentsPreviewModal
          onClose={handleHideCommentPreview}
          parentRef={commentsRef}
          taskId={task.id}
          task={task}
          shouldCheckClose={shouldCheckClose}
        />
      )}
    </>
  )
}

export default React.memo(SubtaskTitle)
